import type { PlanStep, StepResult } from '@verifyloop/core';
import { formatDuration } from '../lib/format';
import { Mono, StepStatusPill, StepTypePill } from './Pills';

/** Read-only rendering of a plan: an ordered list a human can read out loud. */
export function PlanStepsView({
  steps,
  heading = 'Plan',
}: {
  steps: readonly PlanStep[];
  heading?: string;
}): JSX.Element {
  if (steps.length === 0) {
    return <p className="muted">This plan has no steps.</p>;
  }
  return (
    <div className="plan-view">
      <h4 className="plan-view__heading">{heading}</h4>
      <ol className="plan-view__list">
        {steps.map((step, index) => (
          <li key={index} className="plan-view__step">
            <span className="plan-view__index">{index + 1}</span>
            <div className="plan-view__content">
              <div className="plan-view__line">
                <StepTypePill type={step.type} />
                <span className="plan-view__description">{step.description}</span>
                {step.optional ? <span className="pill pill--neutral pill--subtle">optional</span> : null}
              </div>
              {step.target ? (
                <div className="plan-view__line plan-view__line--meta">
                  <span className="plan-view__key">target</span>
                  <Mono>{step.target}</Mono>
                </div>
              ) : null}
              {step.durationMs !== undefined ? (
                <div className="plan-view__line plan-view__line--meta">
                  <span className="plan-view__key">wait</span>
                  <span className="num">{formatDuration(step.durationMs)}</span>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Read-only rendering of what actually happened, in order. */
export function StepResultsView({ steps }: { steps: readonly StepResult[] }): JSX.Element {
  if (steps.length === 0) {
    return <p className="muted">No step results were recorded for this run.</p>;
  }
  return (
    <ol className="step-results">
      {steps.map((step) => (
        <li key={step.index} className={`step-result step-result--${step.status}`}>
          <span className="step-result__index">{step.index + 1}</span>
          <div className="step-result__content">
            <div className="step-result__line">
              <StepStatusPill status={step.status} />
              <StepTypePill type={step.type} />
              <span className="step-result__description">{step.description}</span>
              <span className="step-result__duration num">{formatDuration(step.durationMs)}</span>
            </div>
            {step.resolvedTarget ? (
              <div className="step-result__line step-result__line--meta">
                <span className="step-result__key">resolved</span>
                <Mono>{step.resolvedTarget}</Mono>
              </div>
            ) : null}
            {step.extractedValue !== undefined ? (
              <div className="step-result__line step-result__line--meta">
                <span className="step-result__key">extracted</span>
                <Mono>{safeStringify(step.extractedValue)}</Mono>
              </div>
            ) : null}
            {step.error ? (
              <div className="step-result__line step-result__line--meta">
                <span className="step-result__key">error</span>
                <span className="step-result__error">{step.error}</span>
              </div>
            ) : null}
            {step.artifacts.length > 0 ? (
              <div className="step-result__line step-result__line--meta">
                <span className="step-result__key">artifacts</span>
                <span className="muted">
                  {step.artifacts.map((artifact) => artifact.kind).join(', ')}
                </span>
              </div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

function safeStringify(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}